import useDebounce from 'hooks/useDebounce';
import React, { useEffect, useId, useRef, useState } from 'react';
import { loadPlacesLibrary } from '../utils/googlePlacesLoader';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DELAY_MS = 250;

const getPredictionLabel = (suggestion) => {
  const text = suggestion?.placePrediction?.text;
  return typeof text?.toString === 'function' ? text.toString().trim() : '';
};

const normalizeText = (value) =>
  typeof value?.toString === 'function' ? value.toString().trim() : `${value || ''}`.trim();

const normalizeCoordinate = (value) => (typeof value === 'function' ? value() : value);

const toSelectionPayload = (place, fallbackAddress, fallbackPlaceId) => {
  const lat = normalizeCoordinate(place?.location?.lat);
  const lng = normalizeCoordinate(place?.location?.lng);

  return {
    address:
      normalizeText(place?.formattedAddress) ||
      normalizeText(place?.displayName) ||
      fallbackAddress ||
      '',
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    placeId: place?.id || fallbackPlaceId || '',
  };
};

/**
 * Shared responder autocomplete input backed by the Places Data API.
 * @param {{ stop: any, onPlaceSelect?: function, onManualChange?: function, placeholder?: string, apiKey?: string, disabled?: boolean }} props
 * @returns {JSX.Element}
 */
export default function PlacesAutocompleteInput({
  stop,
  onPlaceSelect,
  onManualChange,
  placeholder = 'Search address...',
  apiKey,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef(null);
  const selectionLabelRef = useRef('');
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [errorMessage, setErrorMessage] = useState('');
  const listboxId = useId();
  const query = stop?.address || '';
  const debouncedQuery = useDebounce(query, SEARCH_DELAY_MS);
  const trimmedQuery = `${query}`.trim();
  const canSearch = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const showNoResults = canSearch && !isLoading && !errorMessage && suggestions.length === 0;
  const showDropdown =
    isFocused &&
    !disabled &&
    canSearch &&
    (isLoading || !!errorMessage || showNoResults || suggestions.length > 0);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    if (disabled) {
      setSuggestions([]);
      setIsLoading(false);
      setErrorMessage('');
      setActiveIndex(-1);
      return;
    }

    const searchTerm = `${debouncedQuery}`.trim();

    if (searchTerm.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      setErrorMessage('');
      setActiveIndex(-1);
      return;
    }

    if (searchTerm === selectionLabelRef.current) {
      setSuggestions([]);
      setIsLoading(false);
      setErrorMessage('');
      setActiveIndex(-1);
      return;
    }

    let isCancelled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const fetchSuggestions = async () => {
      let requestSnapshot = {
        input: searchTerm,
        region: 'us',
        language: null,
        hasSessionToken: false,
      };

      try {
        setIsLoading(true);
        setErrorMessage('');

        const { AutocompleteSessionToken, AutocompleteSuggestion } =
          await loadPlacesLibrary(apiKey);

        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
          input: searchTerm,
          sessionToken: sessionTokenRef.current,
          region: 'us',
        };

        if (typeof navigator !== 'undefined' && navigator.language) {
          request.language = navigator.language;
        }

        requestSnapshot = {
          input: request.input,
          region: request.region,
          language: request.language || null,
          hasSessionToken: Boolean(request.sessionToken),
        };

        const { suggestions: nextSuggestions = [] } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (isCancelled || requestId !== requestIdRef.current) return;

        const placeSuggestions = nextSuggestions.filter(
          (suggestion) => suggestion?.placePrediction && getPredictionLabel(suggestion),
        );

        setSuggestions(placeSuggestions);
        setActiveIndex(placeSuggestions.length ? 0 : -1);
      } catch (error) {
        if (isCancelled || requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setActiveIndex(-1);
        setErrorMessage('Unable to load Google location suggestions.');
      } finally {
        if (!isCancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchSuggestions();

    return () => {
      isCancelled = true;
    };
  }, [apiKey, debouncedQuery, disabled]);

  const handleSuggestionSelect = async (suggestion) => {
    if (disabled || !suggestion?.placePrediction) return;

    const fallbackAddress = getPredictionLabel(suggestion);
    const fallbackPlaceId = suggestion.placePrediction.placeId || '';

    try {
      setIsLoading(true);
      setErrorMessage('');

      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location'],
      });

      const payload = toSelectionPayload(place, fallbackAddress, fallbackPlaceId);
      selectionLabelRef.current = payload.address;
      sessionTokenRef.current = null;
      setSuggestions([]);
      setActiveIndex(-1);
      setIsFocused(false);
      onPlaceSelectRef.current?.(payload);
    } catch (error) {
      setErrorMessage('Unable to load location details for that result.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="position-relative">
      <input
        ref={inputRef}
        className="form-control form-control-sm border-0 responder-input"
        placeholder={placeholder}
        value={query}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          window.setTimeout(() => {
            setIsFocused(false);
          }, 100);
        }}
        onKeyDown={(event) => {
          if (!showDropdown) {
            if (event.key === 'ArrowDown' && suggestions.length > 0) {
              event.preventDefault();
              setIsFocused(true);
              setActiveIndex(0);
            }
            return;
          }

          if (event.key === 'ArrowDown') {
            if (!suggestions.length) return;
            event.preventDefault();
            setActiveIndex((prev) => (prev + 1) % suggestions.length);
            return;
          }

          if (event.key === 'ArrowUp') {
            if (!suggestions.length) return;
            event.preventDefault();
            setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
            return;
          }

          if (event.key === 'Enter' && activeIndex >= 0 && suggestions[activeIndex]) {
            event.preventDefault();
            handleSuggestionSelect(suggestions[activeIndex]);
            return;
          }

          if (event.key === 'Escape') {
            event.preventDefault();
            setSuggestions([]);
            setActiveIndex(-1);
            setIsFocused(false);
          }
        }}
        onChange={(event) => {
          if (disabled) return;
          selectionLabelRef.current = '';
          setIsFocused(true);
          setErrorMessage('');
          onManualChange?.(event.target.value);
        }}
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
      />

      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          className="list-group position-absolute top-100 start-0 end-0 mt-1 shadow-sm z-3 bg-primary-shadow border border-theme-strong rounded overflow-hidden"
          style={{ maxHeight: 240, overflowY: 'auto' }}
        >
          {isLoading && (
            <div className="list-group-item small bg-surface-inset text-theme-muted border-theme">
              Loading...
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="list-group-item small text-danger">{errorMessage}</div>
          )}

          {!isLoading && !errorMessage && showNoResults && (
            <div className="list-group-item small bg-surface-inset text-theme-muted border-theme">
              No matches found.
            </div>
          )}

          {!isLoading &&
            !errorMessage &&
            suggestions.map((suggestion, index) => {
              const label = getPredictionLabel(suggestion);

              return (
                <button
                  key={`${suggestion?.placePrediction?.placeId || label}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`list-group-item list-group-item-action text-start bg-surface-inset text-theme border-theme ${
                    index === activeIndex ? 'active bg-primary-highlight border-0' : ''
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  {label}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
